import { CertGenParams, CertificateData } from '@yandex-int/k-common';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { getCertificatesMentor } from '@yandex-int/k-common/client/api/defs/get-certificates-mentor';
import { H1, Spin, useAppContext, useIntl, useViewDeps } from 'platform-components';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import {
    CertificateCard,
    CERTIFICATE_STATUSES,
    CERTIFICATE_VIEWS,
} from 'common.components/certificate-card/certificate-card';
import CertificatesExpandedParamsModal from 'common.components/certificates-expanded-params-modal/certificates-expanded-params-modal';
import { CertGenExpandedParams } from 'common.components/certificates-expanded-params-modal/typings';
import CertificatesParamsModal from 'common.components/certificates-params-modal/certificates-params-modal';

import { TeacherHeader } from 'common.components/TeacherHeader/TeacherHeader';
import PageContentTitle from 'common.components/page-content-title/page-content-title';
import { getLocalityNameRequest } from 'common.components/user/actions';
import {
    getCertExpandedParamsFromViewedHints,
    getCertParams,
    getSchoolGeoId,
    getSchoolId,
} from 'common.components/user/selectors';

import PortfolioEmpty from './__empty/portfolio__empty';
import HowToPrint from './__how-to-print/portfolio__how-to-print';
import { actions } from './actions';
import {
    getCertificatesParamsLoadingStatus,
    getExpandedCertificatesSlugs,
    getInitialCertificatesData,
    getInitialExpandedCertificatesData,
    getLockedCertificatesSlugs,
    getOpenCertificates,
    getReadyCertificatesLoadingStatus,
} from './selectors';

import './portfolio.scss';

// eslint-disable-next-line complexity
export const Portfolio = () => {
    const intl = useIntl();
    const dispatch = useDispatch();

    const { initialRender } = useAppContext();
    const certificates = useViewDeps(getCertificatesMentor) as CertificateData[] | undefined;

    useMount(() => {
        if (initialRender && certificates) {
            dispatch(actions.setReadyCertificates(certificates));
        } else {
            dispatch(actions.getReadyCertificatesRequest());
        }
    });

    const openCertificates = useSelector(getOpenCertificates);
    const lockedCertificates = useSelector(getLockedCertificatesSlugs);
    const openCertificatesLoadingStatus = useSelector(getReadyCertificatesLoadingStatus);

    const certificatesParamsLoadingStatus = useSelector(getCertificatesParamsLoadingStatus);
    const geoId = useSelector(getSchoolGeoId);
    const certificatesData = useSelector(getInitialCertificatesData);
    const schoolId = useSelector(getSchoolId);
    const hasSavedCertificatesParams = Boolean(useSelector(getCertParams));

    const expandedCertificatesData = useSelector(getInitialExpandedCertificatesData);
    const expandedCertificates = useSelector(getExpandedCertificatesSlugs);
    const hasSavedCertificatesExpandedParams = Boolean(useSelector(getCertExpandedParamsFromViewedHints));

    const [certificatesParamsModalOpened, setCertificatesParamsModalOpened] = useState(
        // показываем модалку только завершившим регистрацию учителям (тем, у которых есть школа)
        !hasSavedCertificatesParams && Boolean(schoolId)
    );

    const [certificatesExpandedParamsModalOpened, setCertificatesExpandedParamsModalOpened] = useState(false);

    useEffect(() => {
        if (!hasSavedCertificatesParams && !certificatesData.locality && geoId) {
            dispatch(getLocalityNameRequest(geoId));
        }
    }, [hasSavedCertificatesParams, certificatesData, geoId, dispatch]);

    const saveCertificatesParams = (certParams: CertGenParams) => {
        dispatch(actions.setCertificatesParamsRequest(certParams));
    };

    const handlePrintButtonClick = () => {
        setCertificatesParamsModalOpened(true);
    };

    const showCertificatesExpandedParamsModal = () => {
        setCertificatesExpandedParamsModalOpened(true);
    };

    const saveCertificatesExpandedParams = (certParams: CertGenExpandedParams) => {
        dispatch(actions.postCertExpandedParamsRequest(certParams));
    };

    // показываем эмпти стейт, если нет карточек сертификатов либо у учителя не завершена регистрация (нет школы)
    const shouldShowEmptyState = (!openCertificates?.length && !lockedCertificates.length) || !schoolId;

    return (
        <>
            <Helmet title={intl.formatMessage({ id: 'teacherLab.portfolio' })} />
            <TeacherHeader hideRightControls />
            <div className="portfolio">
                <PageContentTitle title={<H1 id="teacherPortfolio.header" />} />
                {openCertificatesLoadingStatus === LOADING_STATUSES.LOADING && <Spin />}
                {openCertificatesLoadingStatus === LOADING_STATUSES.SUCCESS && (
                    <>
                        {shouldShowEmptyState ? (
                            <PortfolioEmpty />
                        ) : (
                            <div className="portfolio__content">
                                {openCertificates?.map((slug) => (
                                    <div className="portfolio__content-item" key={slug}>
                                        {expandedCertificates.includes(slug) ? (
                                            <CertificateCard
                                                onPrintButtonClick={
                                                    hasSavedCertificatesExpandedParams
                                                        ? undefined
                                                        : showCertificatesExpandedParamsModal
                                                }
                                                onSecondaryButtonClick={
                                                    hasSavedCertificatesExpandedParams
                                                        ? showCertificatesExpandedParamsModal
                                                        : undefined
                                                }
                                                slug={slug}
                                                status={CERTIFICATE_STATUSES.OPEN}
                                                view={CERTIFICATE_VIEWS.WITH_VERIFICATION}
                                            />
                                        ) : (
                                            <CertificateCard
                                                onPrintButtonClick={
                                                    hasSavedCertificatesParams ? undefined : handlePrintButtonClick
                                                }
                                                slug={slug}
                                                status={CERTIFICATE_STATUSES.OPEN}
                                                view={CERTIFICATE_VIEWS.DEFAULT}
                                            />
                                        )}
                                    </div>
                                ))}
                                {lockedCertificates?.map((slug) => (
                                    <div className="portfolio__content-item" key={slug}>
                                        <CertificateCard
                                            slug={slug}
                                            status={CERTIFICATE_STATUSES.LOCKED}
                                            view={CERTIFICATE_VIEWS.DEFAULT}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        <HowToPrint />
                    </>
                )}
            </div>
            {certificatesParamsModalOpened && (
                <CertificatesParamsModal
                    certificatesData={certificatesData}
                    closeModal={() => setCertificatesParamsModalOpened(false)}
                    onApprove={saveCertificatesParams}
                    progress={certificatesParamsLoadingStatus === LOADING_STATUSES.LOADING}
                />
            )}
            {certificatesExpandedParamsModalOpened && (
                <CertificatesExpandedParamsModal
                    certificatesData={expandedCertificatesData}
                    closeModal={() => setCertificatesExpandedParamsModalOpened(false)}
                    onApprove={saveCertificatesExpandedParams}
                    progress={certificatesParamsLoadingStatus === LOADING_STATUSES.LOADING}
                />
            )}
        </>
    );
};
